import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { useSelector } from 'react-redux'
import { debounce } from 'lodash'

import { getAllSchools } from '../../../utils/api/school'
import { TableParameters } from '../../../utils/entities/tableParameters'
import { OrderBy } from '../../../utils/constants'
import { onSuccess } from '../../../utils/apiHelper'
import SchoolsTable from '../../institutionGroup/SchoolsView/SchoolsTable'
import { selectUser } from '../../../reducers/UserReducer'
import { InstitutionContext } from '../../../Providers/InstitutionProvider'
import { Metadata, retrieveMetadata } from '../../../utils/http'
import SchoolSections from '../../../Components/shared/SchoolSectionsDrawer/SchoolSectionsDrawer'
import { linkSchoolToInstitution, unlinkSchoolFromInstitution } from '../../../utils/api/institution'
import { TableActions } from '../../../Components/antd/Tables/DataTable/utils/actions'
import { GlobalContext } from '../../../Providers/GlobalProvider'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import LinkRequestManagementModal from './LinkRequestManagementModal'

const DEFAULT_DATA = { data: [], meta: new Metadata({ pageSize: 20 }) }

const SchoolsView = () => {
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const iContext = useContext(InstitutionContext)
  const { pendingLinkRequests } = useContext(GlobalContext)

  const [rawSchools, setRawSchools] = useState(DEFAULT_DATA)
  const [tableParameters, setTableParameters] = useState(new TableParameters({ orderBy: OrderBy.NAME, pageSize: 20 }))
  const [selectedSchool, setSelectedSchool] = useState(null)
  const [loading, setLoading] = useState(true)
  const [linkRequestModalVisible, setLinkRequestModalVisible] = useState(false)

  const fetchSchools = useCallback((user, tableParameters) => {
    setLoading(true)

    getAllSchools(user, { ...tableParameters.toQueryParametersOld(), paginate: true }).then(json => {
      if (json?.data || json?.meta) {
        const schools = {}

        schools.data = json?.data ?? []
        schools.meta = json?.meta ? retrieveMetadata(json.meta) : new Metadata()

        setRawSchools(schools)
      }

      setLoading(false)
    })
  }, [getAllSchools, setLoading, setRawSchools])

  const linkToInstitution = useCallback(school => {
    if (iContext?.institution && iContext?.setSchools) {
      linkSchoolToInstitution(user, iContext.institution, school).then(json => {
        if (json?.data) {
          onSuccess(t('schools_view.api.link_school'))
          iContext.setSchools(json.data.schools)
        }
      })
    }
  }, [user, iContext?.institution, iContext?.setSchools, t])

  const unlinkFromInstitution = useCallback(school => {
    if (iContext?.institution && iContext?.setSchools) {
      unlinkSchoolFromInstitution(user, iContext.institution, school).then(json => {
        if (json?.data) {
          onSuccess(t('schools_view.api.unlink_school'))
          iContext.setSchools(json.data.schools)
        }
      })
    }
  }, [user, iContext?.institution, iContext?.setSchools, t])

  const handleTriggerAction = useCallback((school, action) => {
    if (action === TableActions.LINK_INSTITUTION_SCHOOL) {
      linkToInstitution(school)
    } else if (action === TableActions.UNLINK_INSTITUTION_SCHOOL) {
      unlinkFromInstitution(school)
    } else if (action === TableActions.SELECT) {
      setSelectedSchool(school)
    }
  }, [linkToInstitution, unlinkFromInstitution])

  const schools = useMemo(() => {
    const schools = { ...rawSchools }

    schools.data = schools.data.map(s => {
      return { ...s, linked: iContext.schools.some(igs => igs.id === s.id) }
    })

    return schools
  }, [rawSchools, iContext?.schools])

  useEffect(() => {
    const fetch = debounce(() => fetchSchools(user, tableParameters), 300)

    fetch()

    return () => {
      fetch.cancel()
    }
  }, [user.id, tableParameters])

  useEffect(() => {
    if (pendingLinkRequests?.length) {
      setLinkRequestModalVisible(true)
    }
  }, [pendingLinkRequests])

  return (
    <>
      <LinkRequestManagementModal
        visible={linkRequestModalVisible}
        data={pendingLinkRequests}
        onModalClose={() => setLinkRequestModalVisible(false)}
        title={t('schools_table.external_actions.display_link_requests')}
      />
      <SchoolsTable
        data={schools.data}
        help={iContext?.institution?.institutionGroup ? 'data_table.help.missing_schools.contact_admin' : 'data_table.help.missing_schools.contact_opal'}
        loading={loading || iContext.loading}
        linkRequests={pendingLinkRequests}
        metadata={schools.meta}
        openModal={() => setLinkRequestModalVisible(true)}
        parameters={tableParameters}
        onParametersChange={setTableParameters}
        onTriggerAction={handleTriggerAction}
      />
      <SchoolSections
        school={selectedSchool}
        onClose={() => setSelectedSchool(null)}
      />
    </>
  )
}

export default SchoolsView
